import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { apiFetch } from '../../utils/api'
import { generateDECSAL, decsalFilename, type DECSALData, type DECSALEmployeeData } from './decsal'
import { generateRTSXML, rtsXmlFilename, type RTSDeclarationData } from './xml-rts'
import { downloadDTA } from './dta-utils'
import styles from './DeclarationsPage.module.css'

export function DeclarationsPage() {
  const { t } = useTranslation()
  const [activeTab, setActiveTab] = useState<'ccss' | 'acd' | 'adem'>('ccss')
  const [year, setYear] = useState(new Date().getFullYear())
  const [month, setMonth] = useState(new Date().getMonth() + 1)
  const [generating, setGenerating] = useState(false)
  const [lastGenerated, setLastGenerated] = useState<string | null>(null)

  const tabs: Array<{ key: typeof activeTab; label: string }> = [
    { key: 'ccss', label: t('declarations.tab_ccss') },
    { key: 'acd', label: t('declarations.tab_acd') },
    { key: 'adem', label: t('declarations.tab_adem') },
  ]

  // ─── Generate DECSAL ───────────────────────────────────────
  const handleGenerateDECSAL = async () => {
    setGenerating(true)
    try {
      const res = await apiFetch<{ payslips: Array<Record<string, unknown>> }>(
        `/payslips?year=${year}&month=${month}`,
      )
      if (!res.data || res.data.payslips.length === 0) {
        alert(t('declarations.no_payslips_for_period'))
        setGenerating(false)
        return
      }

      const employees: DECSALEmployeeData[] = res.data.payslips.map(p => {
        const details = JSON.parse((p['details_json'] as string) ?? '{}') as Record<string, number>
        const name = (p['employee_name'] as string) ?? ''
        return {
          matriculeCcss: '0000000000000',
          lastName: name.split(' ')[0] ?? '',
          firstName: name.split(' ').slice(1).join(' ') ?? '',
          workingDays: 22,
          grossTotalCents: (p['gross_salary_cents'] as number) ?? 0,
          cappedGrossCents: details['cappedGrossCents'] ?? 0,
          ssEmployeeMaladieCents: details['ssEmployeeMaladieCents'] ?? 0,
          ssEmployeePensionCents: details['ssEmployeePensionCents'] ?? 0,
          ssEmployeeDependanceCents: details['ssEmployeeDependanceCents'] ?? 0,
          ssEmployeeTotalCents: (p['ss_employee_cents'] as number) ?? 0,
          ssEmployerMaladieCents: 0,
          ssEmployerPensionCents: 0,
          ssEmployerAccidentCents: 0,
          ssEmployerMutualiteCents: 0,
          ssEmployerSanteCents: 0,
          ssEmployerTotalCents: 0,
          overtimeHours: details['overtimeHours'] ?? 0,
          overtimeCents: details['overtimeCents'] ?? 0,
        }
      })

      const data: DECSALData = { employerNumber: '0000000000', periodYear: year, periodMonth: month, employees }
      downloadDTA(generateDECSAL(data), decsalFilename(year, month))
      setLastGenerated(`DECSAL ${String(month).padStart(2, '0')}/${year}`)
    } catch {
      alert('Error generating DECSAL')
    }
    setGenerating(false)
  }

  // ─── Generate RTS XML ──────────────────────────────────────
  const handleGenerateRTS = async () => {
    setGenerating(true)
    try {
      const res = await apiFetch<{ payslips: Array<Record<string, unknown>> }>(
        `/payslips?year=${year}&month=${month}`,
      )
      if (!res.data || res.data.payslips.length === 0) {
        alert(t('declarations.no_payslips_for_period'))
        setGenerating(false)
        return
      }

      const rtsData: RTSDeclarationData = {
        employerNumber: '0000000000', employerName: '', employerVatNumber: '',
        periodYear: year, periodMonth: month,
        employees: res.data.payslips.map(p => {
          const details = JSON.parse((p['details_json'] as string) ?? '{}') as Record<string, number>
          return {
            matriculeCcss: '0000000000000',
            lastName: ((p['employee_name'] as string) ?? '').split(' ')[0] ?? '',
            firstName: ((p['employee_name'] as string) ?? '').split(' ').slice(1).join(' ') ?? '',
            birthDate: '', taxClass: '1',
            grossSalaryCents: (p['gross_salary_cents'] as number) ?? 0,
            taxableIncomeCents: details['taxableIncomeCents'] ?? 0,
            rtsCents: (p['tax_rts_cents'] as number) ?? 0,
            creditCISCents: details['creditCISCents'] ?? 0,
            creditCICCents: details['creditCICCents'] ?? 0,
            creditCICO2Cents: details['creditCICO2Cents'] ?? 0,
          }
        }),
      }

      const xml = generateRTSXML(rtsData)
      const blob = new Blob([xml], { type: 'application/xml' })
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = rtsXmlFilename(year, month)
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      URL.revokeObjectURL(url)
      setLastGenerated(`RTS XML ${String(month).padStart(2, '0')}/${year}`)
    } catch {
      alert('Error generating RTS XML')
    }
    setGenerating(false)
  }

  return (
    <div className={styles.page}>
      <div className={styles.header}>
        <h1>{t('declarations.title')}</h1>
        <p className={styles.subtitle}>{t('declarations.subtitle')}</p>
      </div>

      {/* Period selector */}
      <div className={styles.actions}>
        <select className={styles.actionBtn} value={month} onChange={e => setMonth(Number(e.target.value))}>
          {Array.from({ length: 12 }, (_, i) => i + 1).map(m => (
            <option key={m} value={m}>{String(m).padStart(2, '0')}</option>
          ))}
        </select>
        <select className={styles.actionBtn} value={year} onChange={e => setYear(Number(e.target.value))}>
          {[2025, 2026, 2027].map(y => <option key={y} value={y}>{y}</option>)}
        </select>
      </div>

      {/* Tabs */}
      <div className={styles.tabs}>
        {tabs.map(tab => (
          <button key={tab.key} className={`${styles.tab} ${activeTab === tab.key ? styles.tabActive : ''}`} onClick={() => setActiveTab(tab.key)}>
            {tab.label}
          </button>
        ))}
      </div>

      {/* Actions */}
      <div className={styles.actions}>
        {activeTab === 'ccss' && (
          <>
            <button className={styles.actionBtn} onClick={handleGenerateDECSAL} disabled={generating}>
              {t('declarations.generate_decsal')}
            </button>
            <button className={styles.actionBtn} disabled>{t('declarations.generate_decaff')}</button>
            <button className={styles.actionBtn} disabled>{t('declarations.generate_demmat')}</button>
            <button className={styles.actionBtn} disabled>{t('declarations.generate_decmal')}</button>
          </>
        )}
        {activeTab === 'acd' && (
          <>
            <button className={styles.actionBtn} onClick={handleGenerateRTS} disabled={generating}>
              {t('declarations.generate_rts')}
            </button>
            <button className={styles.actionBtn} disabled>{t('declarations.generate_ecsp')}</button>
          </>
        )}
        {activeTab === 'adem' && (
          <button className={styles.actionBtn} disabled>{t('declarations.generate_adem')}</button>
        )}
      </div>

      {lastGenerated && (
        <div className={styles.card}>
          <span className={styles.cardType}>{lastGenerated}</span>
          <span className={styles.cardPeriod}>{t('declarations.status_generated')}</span>
        </div>
      )}

      {generating && <p className={styles.empty}>{t('common.loading')}</p>}
    </div>
  )
}
